import { useCallback, useEffect, useState } from "react";
import {
  BarChart3,
  CalendarDays,
  CheckCircle2,
  CircleDollarSign,
  Globe2,
  Laptop,
  MapPin,
  MessageCircle,
  Moon,
  MousePointerClick,
  PhoneCall,
  Route,
  Smartphone,
  Sun,
  Tablet,
  XCircle,
} from "lucide-react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { supabase } from "../lib/supabaseClient";
import AdminSidebar from "../components/AdminSidebar";
import useAdminTheme from "../hooks/useAdminTheme";
import {
  emptyGa4Metrics,
  fetchGa4Analytics,
} from "../services/analyticsService";

const formatDateKey = (date) => date.toISOString().split("T")[0];

const getStartOfDay = (date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate());

const getEndOfDay = (date) => {
  const end = getStartOfDay(date);
  end.setHours(23, 59, 59, 999);

  return end;
};

const formatTrendLabel = (dateKey) =>
  new Date(`${dateKey}T00:00:00`).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
  });

const getBookingDate = (booking) => new Date(booking.created_at);

const getBookingAmount = (booking) => {
  const amount = Number(booking.final_price || 0);

  return Number.isFinite(amount) ? amount : 0;
};

const getLocationLabel = (location) => {
  const label = String(location || "").trim();

  return label || "Unknown location";
};

const truncateLabel = (label, maxLength = 38) =>
  label.length > maxLength ? `${label.slice(0, maxLength - 3).trim()}...` : label;

const formatLocationName = (location) => {
  const label = getLocationLabel(location);
  const normalizedLabel = label.toLowerCase();

  if (
    normalizedLabel.includes("larnaka international airport") ||
    normalizedLabel.includes("larnaca international airport")
  ) {
    return "Larnaca Airport";
  }

  if (
    normalizedLabel.includes("pafos international airport") ||
    normalizedLabel.includes("paphos international airport")
  ) {
    return "Paphos Airport";
  }

  if (normalizedLabel.includes("limassol marina")) {
    return "Limassol Marina";
  }

  if (normalizedLabel.includes("limassol, cyprus")) {
    return "Limassol";
  }

  if (normalizedLabel.includes("city of dreams")) {
    return "City of Dreams";
  }

  const meaningfulPart =
    label
      .split(",")
      .map((part) => part.trim())
      .find((part) => part && part.toLowerCase() !== "cyprus") || label;

  return truncateLabel(meaningfulPart);
};

const formatRouteName = (pickup, destination) =>
  `${formatLocationName(pickup)} → ${formatLocationName(destination)}`;

const getAirportBucket = (booking) => {
  const routeText = `${booking.pickup || ""} ${booking.destination || ""}`.toLowerCase();

  if (
    routeText.includes("larnaka international airport") ||
    routeText.includes("larnaca international airport") ||
    routeText.includes("larnaca airport")
  ) {
    return "Larnaca Airport";
  }

  if (
    routeText.includes("pafos international airport") ||
    routeText.includes("paphos international airport") ||
    routeText.includes("paphos airport")
  ) {
    return "Paphos Airport";
  }

  return "Other";
};

function AnalyticsPage() {
  const [bookings, setBookings] = useState([]);
  const [periodFilter, setPeriodFilter] = useState("single-day");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [ga4Analytics, setGa4Analytics] = useState({
    configured: false,
    metrics: emptyGa4Metrics,
    topPages: [],
    devices: [],
    channels: [],
    trend: [],
  });
  const [ga4Loading, setGa4Loading] = useState(false);
  const [ga4Error, setGa4Error] = useState("");
  const [selectedDay, setSelectedDay] = useState(
    new Date().toISOString().split("T")[0]
  );
  const { isDark, toggleTheme } = useAdminTheme();

  const fetchBookings = useCallback(async () => {
    // Security assumption: analytics reads are limited by Supabase RLS for the
    // authenticated admin session verified by ProtectedAdminRoute.
    const { data, error } = await supabase
      .from("bookings")
      .select("*")
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Supabase analytics query error:", error);
      return;
    }

    setBookings(data || []);
  }, []);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  useEffect(() => {
    let mounted = true;

    const loadGa4Analytics = async () => {
      setGa4Loading(true);
      setGa4Error("");

      try {
        const analytics = await fetchGa4Analytics({
          period: periodFilter,
          fromDate,
          selectedDay,
          toDate,
        });

        if (mounted) {
          setGa4Analytics(analytics);
        }
      } catch (error) {
        console.error("GA4 analytics fetch error:", error);

        if (mounted) {
          setGa4Error("Unable to load Google Analytics data right now.");
          setGa4Analytics({
            configured: true,
            metrics: emptyGa4Metrics,
            topPages: [],
            devices: [],
            channels: [],
            trend: [],
          });
        }
      } finally {
        if (mounted) {
          setGa4Loading(false);
        }
      }
    };

    loadGa4Analytics();

    return () => {
      mounted = false;
    };
  }, [fromDate, periodFilter, selectedDay, toDate]);

  const changeSelectedDay = (direction) => {
    const date = new Date(selectedDay);
    date.setDate(date.getDate() + direction);

    setSelectedDay(date.toISOString().split("T")[0]);
    setPeriodFilter("single-day");
  };

  const today = new Date();
  const startOfToday = getStartOfDay(today);
  const periodRange = (() => {
    if (periodFilter === "today") {
      return {
        start: startOfToday,
        end: getEndOfDay(today),
        label: "Today",
      };
    }

    if (periodFilter === "week") {
      const start = new Date(startOfToday);
      start.setDate(startOfToday.getDate() - 7);

      return {
        start,
        end: getEndOfDay(today),
        label: "Last 7 days",
      };
    }

    if (periodFilter === "month") {
      return {
        start: new Date(today.getFullYear(), today.getMonth(), 1),
        end: getEndOfDay(today),
        label: "This month",
      };
    }

    if (periodFilter === "year") {
      return {
        start: new Date(today.getFullYear(), 0, 1),
        end: getEndOfDay(today),
        label: "This year",
      };
    }

    if (periodFilter === "single-day" && selectedDay) {
      const start = new Date(selectedDay);
      const end = new Date(selectedDay);
      end.setHours(23, 59, 59, 999);

      return {
        start,
        end,
        label: selectedDay,
      };
    }

    if (periodFilter === "custom" && fromDate && toDate) {
      const start = new Date(fromDate);
      const end = new Date(toDate);
      end.setHours(23, 59, 59, 999);

      return {
        start,
        end,
        label: `${fromDate} to ${toDate}`,
      };
    }

    return {
      start: null,
      end: null,
      label: "All time",
    };
  })();

  const filterByPeriod = (booking) => {
    const bookingDate = getBookingDate(booking);

    if (periodFilter === "custom" && (!fromDate || !toDate)) return false;
    if (periodRange.start && bookingDate < periodRange.start) return false;
    if (periodRange.end && bookingDate > periodRange.end) return false;

    return true;
  };

  const filteredBookings = bookings.filter(filterByPeriod);

  const completedBookings = filteredBookings.filter(
    (booking) => booking.status === "completed"
  );

  const cancelledBookings = filteredBookings.filter(
    (booking) => booking.status === "cancelled"
  );

  const totalRevenue = completedBookings.reduce(
    (sum, booking) => sum + getBookingAmount(booking),
    0
  );

  const averageRideValue =
    completedBookings.length > 0
      ? Math.round(totalRevenue / completedBookings.length)
      : 0;

  const statusData = [
    {
      name: "New",
      value: filteredBookings.filter((b) => b.status === "new").length,
    },
    {
      name: "Confirmed",
      value: filteredBookings.filter((b) => b.status === "confirmed").length,
    },
    {
      name: "Completed",
      value: completedBookings.length,
    },
    {
      name: "Cancelled",
      value: cancelledBookings.length,
    },
  ];

  const getTrendDays = () => {
    if (periodRange.start && periodRange.end) {
      const days = [];
      const cursor = getStartOfDay(periodRange.start);
      const end = getStartOfDay(periodRange.end);

      while (cursor <= end) {
        days.push(formatDateKey(cursor));
        cursor.setDate(cursor.getDate() + 1);
      }

      return days;
    }

    const uniqueDays = [
      ...new Set(
        filteredBookings.map((booking) =>
          formatDateKey(getBookingDate(booking))
        )
      ),
    ].sort();

    return uniqueDays.length > 0 ? uniqueDays : [formatDateKey(today)];
  };

  const trendByDay = getTrendDays().map((dateKey) => ({
    dateKey,
    day: formatTrendLabel(dateKey),
    bookings: 0,
    revenue: 0,
  }));

  filteredBookings.forEach((booking) => {
    const dateKey = formatDateKey(getBookingDate(booking));
    const day = trendByDay.find((item) => item.dateKey === dateKey);

    if (day) {
      day.bookings += 1;
    }
  });

  completedBookings.forEach((booking) => {
    const dateKey = formatDateKey(getBookingDate(booking));
    const day = trendByDay.find((item) => item.dateKey === dateKey);

    if (day) {
      day.revenue += getBookingAmount(booking);
    }
  });

  const topRoutes = filteredBookings.reduce((acc, booking) => {
    const route = formatRouteName(booking.pickup, booking.destination);
    const existing = acc.find((item) => item.route === route);

    if (existing) {
      existing.count += 1;
      existing.revenue +=
        booking.status === "completed" ? getBookingAmount(booking) : 0;
    } else {
      acc.push({
        route,
        count: 1,
        revenue:
          booking.status === "completed" ? getBookingAmount(booking) : 0,
      });
    }

    return acc;
  }, []);

  const sortedTopRoutes = topRoutes
    .sort((a, b) => b.count - a.count || b.revenue - a.revenue)
    .slice(0, 5);

  const topPickupLocations = filteredBookings
    .reduce((acc, booking) => {
      const location = formatLocationName(booking.pickup);
      const existing = acc.find((item) => item.location === location);

      if (existing) {
        existing.count += 1;
      } else {
        acc.push({
          location,
          count: 1,
        });
      }

      return acc;
    }, [])
    .sort((a, b) => b.count - a.count)
    .slice(0, 5);

  const topCustomers = filteredBookings
    .reduce((acc, booking) => {
      const phone = String(booking.phone || "").trim();

      if (!phone) return acc;

      const existing = acc.find((item) => item.phone === phone);
      const revenue =
        booking.status === "completed" ? getBookingAmount(booking) : 0;

      if (existing) {
        existing.count += 1;
        existing.revenue += revenue;
      } else {
        acc.push({
          phone,
          count: 1,
          revenue,
        });
      }

      return acc;
    }, [])
    .sort((a, b) => b.count - a.count || b.revenue - a.revenue)
    .slice(0, 5);

  const airportSplitTotal = filteredBookings.length;
  const airportSplit = ["Larnaca Airport", "Paphos Airport", "Other"].map(
    (name) => {
      const count = filteredBookings.filter(
        (booking) => getAirportBucket(booking) === name
      ).length;
      const percentage =
        airportSplitTotal > 0 ? Math.round((count / airportSplitTotal) * 100) : 0;

      return {
        name,
        count,
        percentage,
      };
    }
  );

  const ga4Metrics = ga4Analytics.metrics || emptyGa4Metrics;
  const ga4Configured = ga4Analytics.configured;
  const ga4HasData =
    Object.values(ga4Metrics).some((value) => Number(value) > 0) ||
    ga4Analytics.topPages.length > 0 ||
    ga4Analytics.devices.length > 0 ||
    ga4Analytics.channels.length > 0;

  const topPages = [...ga4Analytics.topPages]
    .sort((a, b) => b.value - a.value)
    .slice(0, 5)
    .map((page) => ({
      ...page,
      note: page.note || "GA4 page views",
    }));

  const sessionDevices = ga4Analytics.devices.map((device) => ({
    ...device,
    icon:
      device.name.toLowerCase().includes("desktop")
        ? Laptop
        : device.name.toLowerCase().includes("tablet")
          ? Tablet
          : Smartphone,
  }));

  const topChannels = [...ga4Analytics.channels]
    .sort((a, b) => b.value - a.value)
    .slice(0, 5)
    .map((channel) => ({
      ...channel,
      note: channel.note || "GA4 sessions",
    }));

  const bookingSubmissions = ga4Metrics.bookingSubmissions;

  const formatGa4Metric = (value) => {
    if (!ga4Configured) return "Setup";
    if (ga4Loading) return "...";

    return Number(value || 0).toLocaleString("en-GB");
  };

  const webMetrics = [
    {
      label: "Page Views",
      value: formatGa4Metric(ga4Metrics.pageViews),
      detail: "GA4 screenPageViews",
      icon: Globe2,
      accent: "text-blue-600 bg-blue-50",
    },
    {
      label: "Users",
      value: formatGa4Metric(ga4Metrics.users),
      detail: "GA4 activeUsers",
      icon: Smartphone,
      accent: "text-indigo-600 bg-indigo-50",
    },
    {
      label: "Sessions",
      value: formatGa4Metric(ga4Metrics.sessions),
      detail: "GA4 sessions",
      icon: Laptop,
      accent: "text-sky-600 bg-sky-50",
    },
    {
      label: "Booking Submissions",
      value: formatGa4Metric(bookingSubmissions),
      detail: "GA4 booking_submit event",
      icon: MousePointerClick,
      accent: "text-yellow-700 bg-yellow-50",
    },
    {
      label: "Phone Clicks",
      value: formatGa4Metric(ga4Metrics.phoneClicks),
      detail: "GA4 phone_click event",
      icon: PhoneCall,
      accent: "text-emerald-600 bg-emerald-50",
    },
    {
      label: "WhatsApp Clicks",
      value: formatGa4Metric(ga4Metrics.whatsappClicks),
      detail: "GA4 whatsapp_click event",
      icon: MessageCircle,
      accent: "text-green-600 bg-green-50",
    },
  ];

  const businessMetrics = [
    {
      label: "Total Bookings",
      value: filteredBookings.length.toLocaleString("en-GB"),
      icon: CalendarDays,
      detail: "Requests in selected period",
    },
    {
      label: "Total Revenue",
      value: `€${totalRevenue.toLocaleString("en-GB")}`,
      icon: CircleDollarSign,
      detail: "Completed rides with final prices",
    },
    {
      label: "Completed Rides",
      value: completedBookings.length.toLocaleString("en-GB"),
      icon: CheckCircle2,
      detail: "Finished customer trips",
    },
    {
      label: "Cancelled Rides",
      value: cancelledBookings.length.toLocaleString("en-GB"),
      icon: XCircle,
      detail: "Cancelled booking requests",
    },
    {
      label: "Average Ride Value",
      value: `€${averageRideValue}`,
      icon: BarChart3,
      detail: "Revenue per completed ride",
    },
  ];

  const ui = {
    page: isDark ? "bg-[#080808] text-white" : "bg-slate-50 text-slate-950",
    card: isDark
      ? "border-white/10 bg-neutral-950 shadow-2xl shadow-black/30"
      : "border-slate-200 bg-white shadow-sm",
    softCard: isDark
      ? "border-white/10 bg-black/30"
      : "border-slate-100 bg-slate-50",
    strongText: isDark ? "text-white" : "text-slate-950",
    bodyText: isDark ? "text-neutral-300" : "text-slate-700",
    mutedText: isDark ? "text-neutral-400" : "text-slate-500",
    faintText: isDark ? "text-neutral-500" : "text-slate-400",
    input: isDark
      ? "border-white/10 bg-black/40 text-white"
      : "border-slate-200 bg-white text-slate-700",
    yellowBadge: isDark
      ? "border border-yellow-400/20 bg-yellow-400/10 text-yellow-300"
      : "bg-yellow-100 text-yellow-800",
    blueBadge: isDark
      ? "border border-blue-400/20 bg-blue-400/10 text-blue-200"
      : "border border-blue-100 bg-blue-50 text-blue-700",
    gridStroke: isDark ? "#27272a" : "#e2e8f0",
    axisStroke: isDark ? "#a3a3a3" : "#64748b",
    chartPanel: isDark
      ? "bg-gradient-to-b from-neutral-900 to-neutral-950"
      : "bg-gradient-to-b from-blue-50 to-white",
    divide: isDark ? "divide-white/10" : "divide-slate-100",
  };

  return (
    <div className={`min-h-screen ${ui.page}`}>
      <AdminSidebar />

      <main
        className="min-h-screen px-4 py-5 transition-all duration-300 sm:px-6 lg:px-8"
        style={{ marginLeft: "var(--admin-sidebar-width, 16rem)" }}
      >
        <div className="mx-auto max-w-[1500px]">
          <header className={`mb-6 flex flex-col gap-4 rounded-2xl border p-5 sm:p-6 lg:flex-row lg:items-center lg:justify-between ${ui.card}`}>
            <div>
              <div className={`mb-3 flex w-fit items-center gap-2 rounded-full px-3 py-1 text-xs font-bold uppercase tracking-[0.16em] ${ui.yellowBadge}`}>
                <BarChart3 size={14} />
                Analytics
              </div>
              <h1 className={`text-3xl font-black tracking-tight sm:text-4xl ${ui.strongText}`}>
                Analytic overview
              </h1>
              <p className={`mt-2 max-w-2xl text-sm ${ui.mutedText}`}>
                Track booking demand, completed ride revenue, routes, and pickup
                locations.
              </p>
            </div>

            <div className="grid gap-3 sm:grid-cols-[auto_auto_auto_auto] sm:items-center">
              <div className={`flex items-center rounded-xl border p-1 ${ui.softCard}`}>
                <button
                  type="button"
                  onClick={() => changeSelectedDay(-1)}
                  className={`rounded-lg px-3 py-2 text-sm font-bold transition ${ui.bodyText} ${isDark ? "hover:bg-white/10 hover:text-white" : "hover:bg-white hover:text-slate-950"}`}
                >
                  Prev
                </button>
                <input
                  type="date"
                  value={selectedDay}
                  onChange={(e) => {
                    setSelectedDay(e.target.value);
                    setPeriodFilter("single-day");
                  }}
                  className={`h-10 rounded-lg border border-transparent px-3 text-sm font-semibold outline-none focus:border-yellow-400 ${ui.input}`}
                />
                <button
                  type="button"
                  onClick={() => changeSelectedDay(1)}
                  className={`rounded-lg px-3 py-2 text-sm font-bold transition ${ui.bodyText} ${isDark ? "hover:bg-white/10 hover:text-white" : "hover:bg-white hover:text-slate-950"}`}
                >
                  Next
                </button>
              </div>

              <select
                value={periodFilter}
                onChange={(e) => setPeriodFilter(e.target.value)}
                className={`h-12 rounded-xl border px-4 text-sm font-bold outline-none transition focus:border-yellow-400 focus:ring-4 focus:ring-yellow-100 ${ui.input}`}
              >
                <option value="today">Today</option>
                <option value="week">Last 7 days</option>
                <option value="month">Monthly</option>
                <option value="year">This year</option>
                <option value="all">All time</option>
                <option value="single-day">Selected day</option>
                <option value="custom">Custom range</option>
              </select>

              <div className={`flex items-center gap-2 rounded-xl px-4 py-3 text-sm font-bold ${ui.blueBadge}`}>
                <CalendarDays size={17} />
                {periodRange.label}
              </div>

              <button
                type="button"
                onClick={toggleTheme}
                className={`inline-flex items-center justify-center gap-2 rounded-xl border px-4 py-3 text-sm font-black transition ${
                  isDark
                    ? "border-white/10 bg-white/5 text-white hover:bg-white/10"
                    : "border-slate-200 bg-white text-slate-800 hover:border-yellow-300 hover:bg-yellow-50"
                }`}
              >
                {isDark ? <Sun size={17} /> : <Moon size={17} />}
                {isDark ? "Light" : "Dark"}
              </button>
            </div>
          </header>

          {periodFilter === "custom" && (
            <section className={`mb-6 grid gap-3 rounded-2xl border p-4 sm:grid-cols-2 lg:max-w-xl ${ui.card}`}>
              <input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className={`h-12 rounded-xl border px-4 text-sm font-semibold outline-none focus:border-yellow-400 ${ui.input}`}
              />

              <input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className={`h-12 rounded-xl border px-4 text-sm font-semibold outline-none focus:border-yellow-400 ${ui.input}`}
              />
            </section>
          )}

          <section className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
            {businessMetrics.map(({ label, value, detail, icon: Icon }) => (
              <div
                key={label}
                className={`rounded-2xl border p-5 ${ui.card}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <p className={`break-words text-sm font-semibold ${ui.mutedText}`}>
                      {label}
                    </p>
                    <p className={`mt-3 break-words text-3xl font-black tracking-tight ${ui.strongText}`}>
                      {value}
                    </p>
                    <p className={`mt-2 break-words text-xs leading-relaxed ${ui.faintText}`}>
                      {detail}
                    </p>
                  </div>
                  <span className={`shrink-0 rounded-2xl p-3 ${isDark ? "bg-yellow-400/10 text-yellow-300" : "bg-yellow-100 text-yellow-700"}`}>
                    <Icon size={22} />
                  </span>
                </div>
              </div>
            ))}
          </section>

          <section className="mb-6 grid gap-6 xl:grid-cols-2">
            <div className={`rounded-2xl border p-5 sm:p-6 ${ui.card}`}>
              <div className="mb-6">
                <h2 className={`text-xl font-black ${ui.strongText}`}>
                  Booking Trend
                </h2>
                <p className={`mt-1 text-sm ${ui.mutedText}`}>
                  Bookings per day for the selected period.
                </p>
              </div>

              <div className={`h-[320px] rounded-2xl p-3 sm:p-4 ${ui.chartPanel}`}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={trendByDay}>
                    <CartesianGrid strokeDasharray="3 3" stroke={ui.gridStroke} />
                    <XAxis dataKey="day" stroke={ui.axisStroke} tickLine={false} />
                    <YAxis stroke={ui.axisStroke} tickLine={false} allowDecimals={false} />
                    <Tooltip
                      contentStyle={{
                        borderRadius: "14px",
                        border: `1px solid ${isDark ? "#27272a" : "#e2e8f0"}`,
                        background: isDark ? "#09090b" : "#ffffff",
                        color: isDark ? "#ffffff" : "#0f172a",
                        boxShadow: "0 10px 30px rgba(15, 23, 42, 0.12)",
                      }}
                    />
                    <Line
                      type="monotone"
                      dataKey="bookings"
                      stroke="#2563eb"
                      strokeWidth={3}
                      dot={{ r: 4, fill: "#facc15", stroke: "#1d4ed8" }}
                      activeDot={{ r: 6 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className={`rounded-2xl border p-5 sm:p-6 ${ui.card}`}>
              <div className="mb-6">
                <h2 className={`text-xl font-black ${ui.strongText}`}>
                  Revenue Trend
                </h2>
                <p className={`mt-1 text-sm ${ui.mutedText}`}>
                  Completed ride revenue per day for the selected period.
                </p>
              </div>

              <div className={`h-[320px] rounded-2xl p-3 sm:p-4 ${ui.chartPanel}`}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={trendByDay}>
                    <CartesianGrid strokeDasharray="3 3" stroke={ui.gridStroke} />
                    <XAxis dataKey="day" stroke={ui.axisStroke} tickLine={false} />
                    <YAxis stroke={ui.axisStroke} tickLine={false} allowDecimals={false} />
                    <Tooltip
                      formatter={(value) => [`€${Number(value).toLocaleString("en-GB")}`, "Revenue"]}
                      contentStyle={{
                        borderRadius: "14px",
                        border: `1px solid ${isDark ? "#27272a" : "#e2e8f0"}`,
                        background: isDark ? "#09090b" : "#ffffff",
                        color: isDark ? "#ffffff" : "#0f172a",
                        boxShadow: "0 10px 30px rgba(15, 23, 42, 0.12)",
                      }}
                    />
                    <Line
                      type="monotone"
                      dataKey="revenue"
                      stroke="#059669"
                      strokeWidth={3}
                      dot={{ r: 4, fill: "#facc15", stroke: "#047857" }}
                      activeDot={{ r: 6 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </section>

          <section className="grid grid-cols-1 gap-6 xl:grid-cols-2">
            <div className={`rounded-2xl border p-5 sm:p-6 ${ui.card}`}>
              <div className="mb-5 flex items-center justify-between gap-4">
                <div>
                  <h2 className={`text-lg font-black ${ui.strongText}`}>
                    Booking status
                  </h2>
                  <p className={`mt-1 text-sm ${ui.mutedText}`}>
                    Current pipeline for the selected period.
                  </p>
                </div>
              </div>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={statusData}>
                    <CartesianGrid strokeDasharray="3 3" stroke={ui.gridStroke} />
                    <XAxis dataKey="name" stroke={ui.axisStroke} tickLine={false} />
                    <YAxis stroke={ui.axisStroke} tickLine={false} allowDecimals={false} />
                    <Tooltip
                      contentStyle={{
                        borderRadius: "14px",
                        border: `1px solid ${isDark ? "#27272a" : "#e2e8f0"}`,
                        background: isDark ? "#09090b" : "#ffffff",
                        color: isDark ? "#ffffff" : "#0f172a",
                      }}
                    />
                    <Bar dataKey="value" fill="#facc15" radius={[10, 10, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className={`rounded-2xl border p-5 sm:p-6 ${ui.card}`}>
              <div className="mb-5 flex items-center justify-between gap-4">
                <div>
                  <h2 className={`text-lg font-black ${ui.strongText}`}>
                    Top Pickup Locations
                  </h2>
                  <p className={`mt-1 text-sm ${ui.mutedText}`}>
                    Most requested pickup points in the selected period.
                  </p>
                </div>
                <span className={`rounded-2xl p-3 ${isDark ? "bg-yellow-400/10 text-yellow-300" : "bg-yellow-100 text-yellow-700"}`}>
                  <MapPin size={22} />
                </span>
              </div>
              <div className="space-y-3">
                {topPickupLocations.length === 0 ? (
                  <p className={`rounded-xl border px-4 py-3 text-sm font-semibold ${ui.softCard} ${ui.mutedText}`}>
                    No pickup locations for this period.
                  </p>
                ) : (
                  topPickupLocations.map((pickup) => (
                    <div
                      key={pickup.location}
                      className={`flex items-center justify-between gap-4 rounded-xl border px-4 py-3 ${ui.softCard}`}
                    >
                      <p
                        className={`min-w-0 truncate text-sm font-bold ${ui.bodyText}`}
                        title={pickup.location}
                      >
                        {pickup.location}
                      </p>
                      <span className={`shrink-0 rounded-full px-3 py-1 text-sm font-black ${isDark ? "bg-white/10 text-yellow-300" : "bg-white text-yellow-700"}`}>
                        {pickup.count}
                      </span>
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className={`rounded-2xl border p-5 sm:p-6 ${ui.card}`}>
              <div className="mb-5 flex items-center justify-between gap-4">
                <div>
                  <h2 className={`text-lg font-black ${ui.strongText}`}>
                    Top Customers
                  </h2>
                  <p className={`mt-1 text-sm ${ui.mutedText}`}>
                    Most frequent phone numbers in the selected period.
                  </p>
                </div>
                <span className={`rounded-2xl p-3 ${isDark ? "bg-yellow-400/10 text-yellow-300" : "bg-yellow-100 text-yellow-700"}`}>
                  <PhoneCall size={22} />
                </span>
              </div>
              <div className="space-y-3">
                {topCustomers.length === 0 ? (
                  <p className={`rounded-xl border px-4 py-3 text-sm font-semibold ${ui.softCard} ${ui.mutedText}`}>
                    No customer phone numbers for this period.
                  </p>
                ) : (
                  topCustomers.map((customer) => (
                    <div
                      key={customer.phone}
                      className={`flex items-center justify-between gap-4 rounded-xl border px-4 py-3 ${ui.softCard}`}
                    >
                      <div className="min-w-0">
                        <p
                          className={`truncate text-sm font-bold ${ui.bodyText}`}
                          title={customer.phone}
                        >
                          {customer.phone}
                        </p>
                        <p className={`mt-1 text-xs ${ui.faintText}`}>
                          {customer.count} {customer.count === 1 ? "ride" : "rides"}
                        </p>
                      </div>
                      <span className={`shrink-0 rounded-full px-3 py-1 text-sm font-black ${isDark ? "bg-white/10 text-emerald-300" : "bg-white text-emerald-700"}`}>
                        €{customer.revenue.toLocaleString("en-GB")}
                      </span>
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className={`rounded-2xl border p-5 sm:p-6 ${ui.card}`}>
              <div className="mb-5 flex items-center justify-between gap-4">
                <div>
                  <h2 className={`text-lg font-black ${ui.strongText}`}>
                    Airport Split
                  </h2>
                  <p className={`mt-1 text-sm ${ui.mutedText}`}>
                    Airport-related bookings by pickup or destination.
                  </p>
                </div>
                <span className={`rounded-2xl p-3 ${isDark ? "bg-yellow-400/10 text-yellow-300" : "bg-yellow-100 text-yellow-700"}`}>
                  <Route size={22} />
                </span>
              </div>
              <div className="space-y-4">
                {airportSplitTotal === 0 ? (
                  <p className={`rounded-xl border px-4 py-3 text-sm font-semibold ${ui.softCard} ${ui.mutedText}`}>
                    No airport split data for this period.
                  </p>
                ) : (
                  airportSplit.map((item) => (
                    <div key={item.name} className={`rounded-xl border px-4 py-3 ${ui.softCard}`}>
                      <div className="mb-2 flex items-center justify-between gap-4">
                        <p className={`min-w-0 truncate text-sm font-bold ${ui.bodyText}`}>
                          {item.name}
                        </p>
                        <span className={`shrink-0 text-sm font-black ${ui.strongText}`}>
                          {item.count} · {item.percentage}%
                        </span>
                      </div>
                      <div className={`h-2 overflow-hidden rounded-full ${isDark ? "bg-white/10" : "bg-slate-200"}`}>
                        <div
                          className="h-full rounded-full bg-yellow-400"
                          style={{ width: `${item.percentage}%` }}
                        />
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </section>

          <section className={`mt-6 rounded-2xl border p-5 sm:p-6 ${ui.card}`}>
            <div className="mb-5 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <h2 className={`text-lg font-black ${ui.strongText}`}>Top Routes</h2>
                <p className={`mt-1 text-sm ${ui.mutedText}`}>
                  Most booked routes in the selected period.
                </p>
              </div>
              <span className={`flex items-center gap-2 text-sm font-bold ${isDark ? "text-yellow-300" : "text-blue-700"}`}>
                <Route size={17} />
                {sortedTopRoutes.length} routes
              </span>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full min-w-[720px] table-fixed border-collapse">
                <thead>
                  <tr className={`border-b text-left text-xs uppercase tracking-[0.14em] ${isDark ? "border-white/10 text-neutral-500" : "border-slate-200 text-slate-400"}`}>
                    <th className="w-[55%] px-4 py-3 font-bold">Route</th>
                    <th className="w-[20%] px-4 py-3 text-center font-bold">
                      Rides
                    </th>
                    <th className="w-[25%] px-4 py-3 text-right font-bold">
                      Revenue
                    </th>
                  </tr>
                </thead>
                <tbody className={`divide-y ${ui.divide}`}>
                  {sortedTopRoutes.length === 0 ? (
                    <tr>
                      <td
                        colSpan="3"
                        className={`px-4 py-8 text-center text-sm font-semibold ${ui.faintText}`}
                      >
                        No routes for this period.
                      </td>
                    </tr>
                  ) : (
                    sortedTopRoutes.map((route) => (
                      <tr key={route.route}>
                        <td className="px-4 py-4 align-top">
                          <p
                            className={`line-clamp-2 font-bold ${ui.bodyText}`}
                            title={route.route}
                          >
                            {route.route}
                          </p>
                        </td>
                        <td className={`px-4 py-4 text-center align-top font-black ${ui.strongText}`}>
                          {route.count}
                        </td>
                        <td className="px-4 py-4 text-right align-top font-black text-blue-700">
                          €{route.revenue.toLocaleString("en-GB")}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </section>

          <section className={`mt-6 rounded-2xl border p-5 sm:p-6 ${ui.card}`}>
            <div className="mb-5 flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <h2 className={`text-xl font-black ${ui.strongText}`}>
                  Website Analytics
                </h2>
                <p className={`mt-1 text-sm ${ui.mutedText}`}>
                  GA4 website traffic and click events for the selected period.
                </p>
              </div>
              <span className={`w-fit rounded-full px-3 py-1 text-xs font-black ${ui.yellowBadge}`}>
                GA4
              </span>
            </div>

            {!ga4Configured ? (
              <div className={`rounded-2xl border p-5 ${ui.softCard}`}>
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <h3 className={`text-lg font-black ${ui.strongText}`}>
                      Connect Google Analytics 4
                    </h3>
                    <p className={`mt-1 max-w-3xl text-sm ${ui.mutedText}`}>
                      Add a secure GA4 reporting endpoint with
                      <span className="font-bold"> REACT_APP_GA4_ANALYTICS_ENDPOINT</span>
                      {" "}to show users, sessions, page views, and click events.
                      Keep service-account credentials on the server side.
                    </p>
                  </div>
                  <span className={`w-fit rounded-full px-3 py-1 text-xs font-black ${ui.yellowBadge}`}>
                    Setup needed
                  </span>
                </div>
              </div>
            ) : (
              <div className="space-y-5">
                {ga4Loading && (
                  <div className={`rounded-2xl border p-4 text-sm font-bold ${ui.softCard} ${ui.mutedText}`}>
                    Loading Google Analytics metrics...
                  </div>
                )}

                {ga4Error && (
                  <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm font-bold text-red-700">
                    {ga4Error}
                  </div>
                )}

                {!ga4Loading && !ga4Error && !ga4HasData && (
                  <div className={`rounded-2xl border p-5 text-sm font-semibold ${ui.softCard} ${ui.mutedText}`}>
                    Google Analytics is connected, but there is no data for the
                    selected date range yet.
                  </div>
                )}

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
                  {webMetrics.map(({ label, value, detail, icon: Icon, accent }) => (
                    <div
                      key={label}
                      className={`min-w-0 rounded-2xl border p-5 ${ui.softCard}`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <span
                          className={`shrink-0 rounded-xl p-3 ${
                            isDark ? "bg-white/10 text-yellow-300" : accent
                          }`}
                        >
                          <Icon size={20} />
                        </span>
                        <span className={`break-words text-right text-2xl font-black leading-tight ${ui.strongText}`}>
                          {value}
                        </span>
                      </div>
                      <p className={`mt-5 break-words text-sm font-black ${ui.bodyText}`}>
                        {label}
                      </p>
                      <p className={`mt-1 break-words text-xs leading-relaxed ${ui.faintText}`}>
                        {detail}
                      </p>
                    </div>
                  ))}
                </div>

                <div className="grid gap-6 xl:grid-cols-3">
                  <div className={`rounded-2xl border p-5 ${ui.softCard}`}>
                    <h3 className={`text-lg font-black ${ui.strongText}`}>Top pages</h3>
                    <p className={`mt-1 text-sm ${ui.mutedText}`}>
                      Top pages from Google Analytics 4.
                    </p>
                    <div className="mt-5 space-y-3">
                      {topPages.length === 0 ? (
                        <p className={`rounded-xl border px-4 py-3 text-sm font-semibold ${ui.card} ${ui.mutedText}`}>
                          No page data for this range.
                        </p>
                      ) : (
                        topPages.map((page) => (
                          <div
                            key={page.name}
                            className={`flex items-center justify-between gap-4 rounded-xl border px-4 py-3 ${ui.card}`}
                          >
                            <div className="min-w-0">
                              <p className={`truncate text-sm font-bold ${ui.bodyText}`}>
                                {page.name}
                              </p>
                              <p className={`text-xs ${ui.faintText}`}>{page.note}</p>
                            </div>
                            <span className={`rounded-full px-3 py-1 text-sm font-black ${isDark ? "bg-white/10 text-blue-200" : "bg-white text-blue-700"}`}>
                              {page.value}
                            </span>
                          </div>
                        ))
                      )}
                    </div>
                  </div>

                  <div className={`rounded-2xl border p-5 ${ui.softCard}`}>
                    <h3 className={`text-lg font-black ${ui.strongText}`}>
                      Session devices
                    </h3>
                    <p className={`mt-1 text-sm ${ui.mutedText}`}>
                      Session device split from Google Analytics 4.
                    </p>
                    <div className="mt-5 space-y-3">
                      {sessionDevices.length === 0 ? (
                        <p className={`rounded-xl border px-4 py-3 text-sm font-semibold ${ui.card} ${ui.mutedText}`}>
                          No device data for this range.
                        </p>
                      ) : (
                        sessionDevices.map((device) => {
                          const Icon = device.icon || Smartphone;

                          return (
                            <div
                              key={device.name}
                              className={`flex items-center justify-between gap-4 rounded-xl border px-4 py-3 ${ui.card}`}
                            >
                              <div className="flex min-w-0 items-center gap-3">
                                <span className={`rounded-xl p-2 ${isDark ? "bg-white/10 text-neutral-400" : "bg-white text-slate-500"}`}>
                                  <Icon size={17} />
                                </span>
                                <p className={`truncate text-sm font-bold ${ui.bodyText}`}>
                                  {device.name}
                                </p>
                              </div>
                              <span className={`text-sm font-black ${ui.strongText}`}>
                                {device.value}
                              </span>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>

                  <div className={`rounded-2xl border p-5 ${ui.softCard}`}>
                    <h3 className={`text-lg font-black ${ui.strongText}`}>
                      Top channels
                    </h3>
                    <p className={`mt-1 text-sm ${ui.mutedText}`}>
                      Top traffic channels from Google Analytics 4.
                    </p>
                    <div className="mt-5 space-y-3">
                      {topChannels.length === 0 ? (
                        <p className={`rounded-xl border px-4 py-3 text-sm font-semibold ${ui.card} ${ui.mutedText}`}>
                          No channel data for this range.
                        </p>
                      ) : (
                        topChannels.map((channel) => (
                          <div
                            key={channel.name}
                            className={`flex items-center justify-between gap-4 rounded-xl border px-4 py-3 ${ui.card}`}
                          >
                            <div className="min-w-0">
                              <p className={`truncate text-sm font-bold ${ui.bodyText}`}>
                                {channel.name}
                              </p>
                              <p className={`text-xs ${ui.faintText}`}>{channel.note}</p>
                            </div>
                            <span className={`rounded-full px-3 py-1 text-sm font-black ${isDark ? "bg-white/10 text-yellow-300" : "bg-white text-yellow-700"}`}>
                              {channel.value}
                            </span>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </section>
        </div>
      </main>
    </div>
  );
}

export default AnalyticsPage;
